import { EditOrderGuideActionType } from '@/experiences/sales-portal/domain/order-guides/blocs/reducers/use-edit-order-guide-reducer';
import { isEqual } from 'lodash';
import { OrderGuide } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useEditOrderGuideActions,
  useEditOrderGuideState,
} from '@/experiences/sales-portal/domain/order-guides/blocs/contexts/edit-order-guide-context';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useIsMounted } from '@/experiences/sales-portal/hooks/use-is-mounted';
import { useSetOrderGuideCollectionMutator } from '@/experiences/sales-portal/domain/order-guides/blocs/mutators/use-set-order-guide-collection-mutator';

interface UseAutosaveOptions {
  lastSavedOrderGuide: OrderGuide;
  restaurantUUID: string;
  supplierUUID: string;
}

/**
 * Autosaves edits: whenever the editor state diverges from the last saved
 * snapshot, it replaces the whole collection.
 *
 * Saves run one at a time. If the editor changes during an in-flight save,
 * exactly one follow-up save runs once it settles, always with the latest
 * state.
 *
 * On success the saved snapshot advances locally. On failure it reverts the editor
 * to the last saved snapshot and toasts.
 *
 * @param options.lastSavedOrderGuide - Loader's last saved order guide snapshot; the save baseline and revert target.
 * @param options.restaurantUUID - Restaurant the collection belongs to.
 * @param options.supplierUUID - Supplier the collection belongs to.
 */
export const useAutosave = ({
  lastSavedOrderGuide,
  restaurantUUID,
  supplierUUID,
}: UseAutosaveOptions): void => {
  const { collectionUUID, isDefault, name, partitions } =
    useEditOrderGuideState();
  const dispatch = useEditOrderGuideActions();
  const { showErrorToast } = useExperienceToasts();
  const isMounted = useIsMounted();
  const { commit } = useSetOrderGuideCollectionMutator({
    restaurantUUID,
    supplierUUID,
  });

  // Drop selectedPartitionUUID so switching groups isn't seen as an edit.
  const editorState = useMemo<OrderGuide>(
    () => ({ collectionUUID, isDefault, name, partitions }),
    [collectionUUID, isDefault, name, partitions],
  );

  const lastSavedRef = useRef(lastSavedOrderGuide);

  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;

  const saveInFlightRef = useRef(false);

  const save = useCallback(async () => {
    if (saveInFlightRef.current) {
      return;
    }
    const target = editorStateRef.current;
    if (isEqual(target, lastSavedRef.current)) {
      return;
    }

    saveInFlightRef.current = true;
    const saved = await commit(target);
    saveInFlightRef.current = false;

    if (!isMounted()) {
      return;
    }

    if (!saved) {
      showErrorToast();
      dispatch({
        payload: { hydratedState: lastSavedRef.current },
        type: EditOrderGuideActionType.HYDRATE,
      });
      return;
    }

    lastSavedRef.current = target;
    // Persist any edits that landed while this save was in flight.
    void save();
  }, [commit, dispatch, isMounted, showErrorToast]);

  useEffect(() => {
    void save();
  }, [editorState, save]);
};
